import { ref, uploadBytesResumable, getDownloadURL, deleteObject, getBlob } from 'firebase/storage';
import { storage } from '@/config/firebase';

/**
 * Sube un archivo de un proyecto de innovación a Firebase Storage.
 * Retorna la URL pública de descarga.
 * 
 * @param file El objeto File seleccionado por el usuario.
 * @param projectId El ID del proyecto para organizar la carpeta.
 */
export async function uploadInnovationProjectFile(file: File, projectId: string): Promise<string> {
  if (!file || !projectId) {
    throw new Error('File and Project ID are required');
  }

  // Generamos un nombre único para evitar colisiones
  const uniqueFileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
  
  // Ruta en Storage: innovation_projects/{projectId}/tracking/{fileName}
  const storageRef = ref(storage, `innovation_projects/${projectId}/tracking/${uniqueFileName}`);

  try {
    const snapshot = await uploadBytesResumable(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading file to Storage:', error);
    throw error;
  }
}

/**
 * Sube un archivo/foto de un Update de innovación a Firebase Storage.
 * Retorna la URL pública de descarga.
 * 
 * @param file El objeto File seleccionado por el usuario.
 * @param projectId El ID del proyecto para organizar la carpeta.
 * @param updateId Un identificador único para el update (ej. timestamp).
 */
export async function uploadInnovationUpdateFile(file: File, projectId: string, updateId: string): Promise<string> {
  if (!file || !projectId || !updateId) {
    throw new Error('File, Project ID and Update ID are required');
  }

  const uniqueFileName = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
  
  // Ruta en Storage: innovation_projects/{projectId}/updates/{updateId}/{fileName}
  const storageRef = ref(storage, `innovation_projects/${projectId}/updates/${updateId.replace(/[\/:]/g, '-')}/${uniqueFileName}`);

  try {
    const snapshot = await uploadBytesResumable(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading update file to Storage:', error);
    throw error;
  }
}

/**
 * Borra un archivo de Firebase Storage usando su URL.
 * Sirve tanto para Tracking files como para Update files.
 * 
 * @param fileUrl La URL de descarga del archivo a eliminar.
 */
export async function deleteInnovationProjectFile(fileUrl: string): Promise<void> {
  if (!fileUrl) return;

  try {
    // Si la URL no es de Firebase Storage, la ignoramos (ej: string de "Legacy")
    if (!fileUrl.includes('firebasestorage')) {
      return;
    }
    
    const fileRef = ref(storage, fileUrl);
    await deleteObject(fileRef);
  } catch (error: any) {
    // Si el archivo ya no existe, no lanzamos error
    if (error.code === 'storage/object-not-found') {
      console.warn('File already deleted or not found in Storage');
      return;
    }
    console.error('Error deleting file from Storage:', error);
    throw error;
  }
}

export async function downloadInnovationProjectFileBlob(fileUrl: string): Promise<Blob> {
  if (!fileUrl) {
    throw new Error('fileUrl is required');
  }

  const fileRef = ref(storage, fileUrl);
  return getBlob(fileRef);
}
